import type {
  InvoiceKind,
  InvoicePeriod,
  InvoiceSortDir,
  InvoiceSortKey,
  InvoiceStatusFilter,
} from "./invoice-list";

/**
 * Filtrene på /faktura står i adressen med norske verdier
 * (`/faktura?status=forfalt`), så en lenke kan deles og overlever refresh
 * (VIB-2948). Standardverdier skrives ikke, så standardvisningen er `/faktura`.
 */
export interface InvoiceListParams {
  q: string;
  status: InvoiceStatusFilter;
  type: InvoiceKind | "all";
  period: InvoicePeriod;
  from: string | null;
  to: string | null;
  sort: InvoiceSortKey;
  dir: InvoiceSortDir;
  page: number;
}

export const DEFAULT_INVOICE_LIST_PARAMS: InvoiceListParams = {
  q: "",
  status: "all",
  type: "all",
  period: "ytd",
  from: null,
  to: null,
  sort: "invoice_date",
  dir: "desc",
  page: 1,
};

const STATUS_SLUGS: Record<Exclude<InvoiceStatusFilter, "all">, string> = {
  draft: "utkast",
  outstanding: "utestaende",
  sent: "sendt",
  overdue: "forfalt",
  paid: "betalt",
  credited: "kreditert",
  credit_note: "kreditnota",
};
const TYPE_SLUGS: Record<InvoiceKind, string> = {
  startup: "oppstart",
  interim: "mellomfaktura",
  final: "slutt",
  upsell: "mersalg",
  credit_note: "kreditnota",
  poweroffice: "poweroffice",
};
const PERIOD_SLUGS: Record<Exclude<InvoicePeriod, "ytd">, string> = {
  last_year: "i-fjor",
  last_12m: "siste-12-mnd",
  all: "alle",
  custom: "egendefinert",
};
const SORT_SLUGS: Record<InvoiceSortKey, string> = {
  invoice_date: "dato",
  project: "prosjekt",
  customer: "kunde",
  amount: "belop",
  due_date: "forfall",
};

function fromSlug<K extends string>(map: Record<K, string>, slug: string | null): K | undefined {
  if (!slug) return undefined;
  return (Object.keys(map) as K[]).find((k) => map[k] === slug);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function parseInvoiceListParams(sp: URLSearchParams): InvoiceListParams {
  const d = DEFAULT_INVOICE_LIST_PARAMS;
  const page = Number(sp.get("side"));
  const from = sp.get("fra");
  const to = sp.get("til");
  return {
    q: sp.get("q") ?? d.q,
    status: fromSlug(STATUS_SLUGS, sp.get("status")) ?? d.status,
    type: fromSlug(TYPE_SLUGS, sp.get("type")) ?? d.type,
    period: fromSlug(PERIOD_SLUGS, sp.get("periode")) ?? d.period,
    from: from && ISO_DATE.test(from) ? from : null,
    to: to && ISO_DATE.test(to) ? to : null,
    sort: fromSlug(SORT_SLUGS, sp.get("sorter")) ?? d.sort,
    dir: sp.get("retning") === "opp" ? "asc" : sp.get("retning") === "ned" ? "desc" : d.dir,
    page: Number.isInteger(page) && page > 1 ? page : d.page,
  };
}

export function serializeInvoiceListParams(p: InvoiceListParams): URLSearchParams {
  const d = DEFAULT_INVOICE_LIST_PARAMS;
  const sp = new URLSearchParams();
  if (p.q) sp.set("q", p.q);
  if (p.status !== "all") sp.set("status", STATUS_SLUGS[p.status]);
  if (p.type !== "all") sp.set("type", TYPE_SLUGS[p.type]);
  if (p.period !== "ytd") sp.set("periode", PERIOD_SLUGS[p.period]);
  if (p.period === "custom" && p.from) sp.set("fra", p.from);
  if (p.period === "custom" && p.to) sp.set("til", p.to);
  if (p.sort !== d.sort || p.dir !== d.dir) {
    sp.set("sorter", SORT_SLUGS[p.sort]);
    sp.set("retning", p.dir === "asc" ? "opp" : "ned");
  }
  if (p.page > 1) sp.set("side", String(p.page));
  return sp;
}
